import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { fetchAllData, fetchData, fetchDeleteData, fetchUpdateData } from '../../helpers/externapi';
import { fetchConfigValues } from '../../Commoncomponents/CommonComponents';
import { Box, Paper, Typography } from '@mui/material';
import CommunityGroupCustomers from './CommunityGroupCustomers';

const GroupPersonalDetails = () => {
    const { Id } = useParams();
    const navigate = useNavigate();
    const [groupData, setGroupData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [imageUrl, setImageUrl] = useState(null);

    // Product management states
    const [allProducts, setAllProducts] = useState([]);
    const [groupProducts, setGroupProducts] = useState([]);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [productFilter, setProductFilter] = useState('all');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [showAddProducts, setShowAddProducts] = useState(false);
    const [productLoading, setProductLoading] = useState(false);
    const [productError, setProductError] = useState(null);
    const sectionRef = useRef(null);
    const [customerCount, setCustomerCount] = useState(0);
    const [groupAdvisors, setGroupAdvisors] = useState([]);
    const [allAdvisors, setAllAdvisors] = useState([]);
    const [activeTab, setActiveTab] = useState('active');
    const [showAddAdvisors, setShowAddAdvisors] = useState(false);
    const [selectedAdvisors, setSelectedAdvisors] = useState([]);
    const [advisorLoading, setAdvisorLoading] = useState(false);

    // New states for tabs
    const [activeAdviosrTab, setActiveAdviosrTab] = useState('active'); // 'active' or 'inactive'
    const [comboFilter, setComboFilter] = useState('all'); // 'all', 'individual', 'combo'

    useEffect(() => {
        if (showAddProducts && sectionRef.current) {
            sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [showAddProducts]);

    useEffect(() => {
        const fetchGroupData = async () => {
            try {
                setLoading(true);
                const response = await fetchAllData(`lambdaAPI/Group/GetById/${Id}`);
                setGroupData(response[0]);
                setError(null);
            } catch (err) {
                setError(err.message);
                console.error('Error fetching group data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchGroupData();
    }, [Id]);

    useEffect(() => {
        const fetchCustomerCount = async () => {
            try {
                const response = await fetchAllData(
                    `lambdaAPI/CommunityCustomers/GetCountOfMembersByGroupId/${Id}`
                );
                setCustomerCount(response.data || 0);
            } catch (error) {
                console.error("Error fetching customer count:", error);
            }
        };

        fetchCustomerCount();
    }, [Id]);

    useEffect(() => {
        const getMocUrl = async () => {
            const { userImagesBucketlink } = await fetchConfigValues();
            if (userImagesBucketlink) {
                setImageUrl(userImagesBucketlink);
            }
        };

        getMocUrl();
    }, []);

    // Fetch products with new API
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setProductLoading(true);

                // Fetch all products for adding
                const allProdsResponse = await fetchData("Products/all", { skip: 0, take: 0 });
                setAllProducts(allProdsResponse || []);

                // Fetch group products with new API
                await fetchGroupProducts();

                setProductError(null);
            } catch (err) {
                setProductError(err.message);
                console.error('Error fetching products:', err);
            } finally {
                setProductLoading(false);
            }
        };

        if (Id) {
            fetchProducts();
        }
    }, [Id]);

    // New function to fetch group products based on filters
    const fetchGroupProducts = async () => {
        try {
            const payload = {
                groupId: parseInt(Id),
                isActive: activeTab === 'active'
            };

            // Add isCombo filter if not 'all'
            if (comboFilter !== 'all') {
                payload.isCombo = comboFilter === 'combo';
            }

            const response = await fetchData("lambdaAPI/GroupProducts/FetchGroupProducts", payload);
            setGroupProducts(response.data || []);
        } catch (err) {
            console.error('Error fetching group products:', err);
            setProductError(err.message);
        }
    };

    // Refetch products when filters change
    useEffect(() => {
        if (Id) {
            fetchGroupProducts();
        }
    }, [Id, activeTab, comboFilter]);

    useEffect(() => {
        const groupProductIds = groupProducts.map(prod => prod.ProductsId);
        const available = allProducts.filter(prod => !groupProductIds.includes(prod.ProductsId));
        setAvailableProducts(available);
    }, [allProducts, groupProducts]);

    useEffect(() => {
        let filtered = availableProducts;
        if (productFilter === "combo") {
            filtered = availableProducts.filter(product => product.IsCombo);
        } else if (productFilter === "individual") {
            filtered = availableProducts.filter(product => !product.IsCombo);
        }
        setFilteredProducts(filtered);
        setSelectedProducts([]);
    }, [productFilter, availableProducts]);


    // const filteredAdvisors = groupAdvisors.filter(advisor =>
    //     activeAdviosrTab === 'active' ? advisor.IsGroupAdvisorActive : !advisor.IsGroupAdvisorActive
    // );

    // Get available advisors (not already in group)
    // const availableAdvisors = allAdvisors.filter(advisor =>
    //     !groupAdvisors.some(groupAdvisor => groupAdvisor.EmployeeId === advisor.EmployeeId)
    // );

    // Fetch group advisors
    const fetchGroupAdvisors = async () => {
        try {
            const response = await fetchData('lambdaAPI/GroupAdvisors/FetchGroupAdvisors', {
                groupId: parseInt(Id),
                isActive: true
            });

            if (response) {
                setGroupAdvisors(response.data || []);
            }
        } catch (err) {
            setError('Error fetching group advisors: ' + err.message);
        }
    };

    // Fetch all advisors
    const fetchAllAdvisors = async () => {
        try {
            const response = await fetchData('lambdaAPI/Employee/GetAdvisors', {
                skip: 0,
                take: 0,
                userRoleId: 33,
                isActive: true
            });

            setAllAdvisors(response || []);
        } catch (err) {
            console.error('Error fetching advisors:', err);
        }
    };

    // Handle advisor selection
    const handleAdvisorSelect = (employeeId) => {
        setSelectedAdvisors(prev =>
            prev.includes(employeeId)
                ? prev.filter(id => id !== employeeId)
                : [...prev, employeeId]
        );
    };

    // Add selected advisors to group
    const handleAddAdvisors = async () => {
        setAdvisorLoading(true);
        try {
            const payload = {
                groupId: parseInt(Id),
                employeeId: selectedAdvisors, // send full array
            };

            const response = await fetchData(
                "lambdaAPI/GroupAdvisors/add",
                payload
            );
            console.log("response", response);

            // Refresh data
            await fetchGroupAdvisors();
            setShowAddAdvisors(false);
            setSelectedAdvisors([]);
            setError("");
        } catch (err) {
            setError("Error adding advisors: " + err.message);
        } finally {
            setAdvisorLoading(false);
        }
    };


    // Remove/Deactivate advisor from group
    const handleRemoveAdvisor = async (groupAdvisorId) => {
        if (!window.confirm('Are you sure you want to remove this advisor from the group?')) {
            return;
        }

        try {
            const response = await fetchDeleteData(`lambdaAPI/GroupAdvisors/deleteGroupAdvisor/${groupAdvisorId}`);
            if (response) {
                await fetchGroupAdvisors();
                setError('');
            } else {
                setError('Failed to remove advisor');
            }
        } catch (err) {
            setError('Error removing advisor: ' + err.message);
        }
    };

    // Activate advisor
    const handleActivateAdvisor = async (groupAdvisorId) => {
        try {
            const updatePayload = [{
                columnName: "IsActive",
                columnValue: 1,
                tableName: "GroupAdvisors",
                tableId: groupAdvisorId,
            }];

            const response = await fetchUpdateData('lambdaAPI/Update/CommonUpdate', updatePayload);

            if (response.status === true) {
                await fetchGroupAdvisors();
                setError('');
            }
        } catch (err) {
            setError('Error activating advisor: ' + err.message);
        }
    };

    // Scroll to add section when it opens
    useEffect(() => {
        if (showAddAdvisors && sectionRef.current) {
            sectionRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [showAddAdvisors]);

    // Load data on component mount
    useEffect(() => {
        fetchGroupAdvisors();
        fetchAllAdvisors();
    }, []);


    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    const handleProductSelect = (productId) => {
        setSelectedProducts(prev => {
            if (prev.includes(productId)) {
                return prev.filter(id => id !== productId);
            } else {
                return [...prev, productId];
            }
        });
    };

    const handleAddProducts = async () => {
        if (selectedProducts.length === 0) return;
        try {
            setProductLoading(true);
            const payload = {
                groupId: parseInt(Id),
                productsId: selectedProducts
            };
            await fetchData("lambdaAPI/GroupProducts/add", payload);

            // Refresh group products
            await fetchGroupProducts();

            setSelectedProducts([]);
            setShowAddProducts(false);
            setProductError(null);
        } catch (err) {
            setProductError(err.message);
            console.error('Error adding products:', err);
        } finally {
            setProductLoading(false);
        }
    };

    const handleRemoveProduct = async (GroupProductsId) => {
        if (window.confirm('Are you sure you want to remove this product from the group?')) {
            try {
                setProductLoading(true);
                await fetchDeleteData(`lambdaAPI/GroupProducts/delete/${GroupProductsId}`);

                // Refresh group products
                await fetchGroupProducts();

                setProductError(null);
            } catch (err) {
                setProductError(err.message);
                console.error('Error removing product:', err);
            } finally {
                setProductLoading(false);
            }
        }
    };

    const handleProductDetailsNavigation = (productId) => {
        navigate(`/products/details/${productId}`);
    };

    if (loading) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="text-dark">Loading group details...</h5>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="alert alert-danger text-center" style={{ maxWidth: '400px', borderRadius: '8px' }}>
                    <h5>Error Loading Data</h5>
                    <p className="mb-0">{error}</p>
                    <button className="btn btn-outline-danger mt-3" onClick={() => window.location.reload()}>
                        <i className="fas fa-refresh me-2"></i>Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (!groupData) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="alert alert-warning text-center" style={{ maxWidth: '400px', borderRadius: '8px' }}>
                    <h5>No Data Found</h5>
                    <p className="mb-0">Group information not available</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            padding: '2rem',
            backgroundColor: '#f8f9fa',
            minHeight: '100vh',
            fontFamily: "'Inter', sans-serif"
        }}>
            <div className="container-fluid">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center">
                        <button
                            className="btn btn-outline-secondary me-3 d-flex align-items-center justify-content-center"
                            style={{ borderRadius: '8px', width: '40px', height: '40px' }}
                            onClick={() => navigate(-1)}
                        >
                            <i className="fas fa-arrow-left"></i>
                        </button>
                        <div>
                            <h1 className="mb-1" style={{ fontSize: '1.875rem', fontWeight: '600', color: '#1a202c' }}>
                                Group Details
                            </h1>
                            <p className="mb-0 text-muted">Manage group information and products</p>
                        </div>
                    </div>
                    <div className="d-flex gap-3">
                        {/* Customers Button */}
                        <button
                            className="btn btn-primary d-flex align-items-center"
                            onClick={() => navigate(`/Communities/Group/customers/${Id}`)}
                        >
                            <i className="fas fa-users me-2"></i>
                            {groupData.GroupName} Customers ({customerCount})
                        </button>

                        {/* Add Products Button */}
                        <button
                            className={`btn d-flex align-items-center px-4 py-2 ${showAddProducts ? "btn-outline-secondary" : "btn-primary"
                                }`}
                            style={{ borderRadius: "8px", fontWeight: "500" }}
                            onClick={() => setShowAddProducts(!showAddProducts)}
                        >
                            <i
                                className={`fas ${showAddProducts ? "fa-times" : "fa-plus"} me-2`}
                            ></i>
                            {showAddProducts ? "Cancel" : "Add Products"}
                        </button>

                        <button
                            className="btn btn-primary d-flex align-items-center"
                            onClick={() => setShowAddAdvisors(true)}
                            style={{ borderRadius: '8px', fontSize: '13px' }}
                        >
                            <i className="fas fa-user-plus me-2"></i>
                            Add Advisors
                        </button>


                    </div>
                </div>

                {/* Main Content */}
                <div className="row g-4">
                    {/* Group Information Card */}
                    <div className="col-12">
                        <div className="card shadow-sm border-0" style={{ borderRadius: '12px' }}>
                            {/* Header */}
                            <div className="card-header bg-white border-bottom" style={{ padding: '2rem', borderRadius: '12px 12px 0 0' }}>
                                <div className="row align-items-center">
                                    <div className="col-md-8">
                                        <div className="d-flex align-items-center">
                                            <div style={{
                                                width: "80px",
                                                height: "80px",
                                                borderRadius: "12px",
                                                backgroundColor: "#e2e8f0",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                marginRight: '20px',
                                                overflow: "hidden"
                                            }}>
                                                {groupData.GroupLogo ? (
                                                    <img
                                                        src={`${imageUrl}${groupData.GroupLogo}`}
                                                        alt="Group Logo"
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                        }}
                                                    />
                                                ) : (
                                                    <span style={{ fontSize: '24px', fontWeight: '600', color: '#64748b' }}>
                                                        {groupData.GroupName?.charAt(0).toUpperCase() || "G"}
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                <h2 className="mb-2" style={{ fontWeight: '600', color: '#1a202c', fontSize: '1.75rem' }}>
                                                    {groupData.GroupName}
                                                </h2>
                                                <p className="mb-2 text-muted d-flex align-items-center">
                                                    <i className="fas fa-map-marker-alt me-2"></i>
                                                    {groupData.GroupAddress}
                                                </p>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="badge bg-light text-dark px-3 py-2" style={{ fontWeight: '500' }}>
                                                        {groupData.Code}
                                                    </span>
                                                    <span className={`badge px-3 py-2 ${groupData.IsActive ? 'bg-success' : 'bg-danger'}`}
                                                        style={{ fontWeight: '500' }}>
                                                        {groupData.IsActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4 text-md-end">
                                        <div className="text-center text-md-end">
                                            <h3 className="mb-1" style={{ fontWeight: '600', color: '#1a202c' }}>
                                                {groupProducts.length}
                                            </h3>
                                            <p className="text-muted mb-0">Active Products</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="card border rounded-4 p-3 w-100 mt-4">
                                <div className="row text-center g-3">
                                    {/* Staff */}
                                    <div className="col">
                                        <div className="d-flex flex-column align-items-center">
                                            <div className="icon-circle  bg-opacity-10 text-primary mb-2">
                                                <i className="bi bi-people-fill"></i>
                                            </div>
                                            <h5 className="fw-bold mb-0">{groupData.NoOfStaff || 0}</h5>
                                            <small className="text-muted">Staff</small>
                                        </div>
                                    </div>

                                    {/* Created Date */}
                                    <div className="col">
                                        <div className="d-flex flex-column align-items-center">
                                            <div className="icon-circle bg-opacity-10 text-success mb-2">
                                                <i className="bi bi-calendar-check-fill"></i>
                                            </div>
                                            <div className="fw-semibold">{formatDate(groupData.CreatedDate)}</div>
                                            <small className="text-muted">Created</small>
                                        </div>
                                    </div>

                                    {/* Expired Date */}
                                    <div className="col">
                                        <div className="d-flex flex-column align-items-center">
                                            <div className="icon-circle bg-opacity-10 text-danger mb-2">
                                                <i className="bi bi-calendar-x-fill"></i>
                                            </div>
                                            <div className="fw-semibold">{groupData.ExpiredDate ? formatDate(groupData.ExpiredDate) : "No ExpiredDate"}</div>
                                            <small className="text-muted">Expires</small>
                                        </div>
                                    </div>

                                    {/* Director */}
                                    <div className="col">
                                        <div className="d-flex flex-column align-items-center">
                                            <div className="icon-circle bg-opacity-10 text-warning mb-2">
                                                <i className="bi bi-person-badge-fill"></i>
                                            </div>
                                            <div className="fw-semibold">{groupData.DirectorName || "N/A"}</div>
                                            <small className="text-muted">Director</small>
                                        </div>
                                    </div>

                                    {/* Products */}
                                    <div className="col">
                                        <div className="d-flex flex-column align-items-center">
                                            <div className="icon-circle  bg-opacity-10 text-info mb-2">
                                                <i className="bi bi-box-seam-fill"></i>
                                            </div>
                                            <h5 className="fw-bold mb-0">{groupProducts.length}</h5>
                                            <small className="text-muted">Products</small>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Information Grid */}
                            <div className="card-body">
                                <div className="row g-4">
                                    {/* Contact Information */}
                                    <div className="col-lg-6">
                                        <div className="card border h-100" style={{ borderRadius: '8px' }}>
                                            <div className="card-header bg-white border-bottom px-4 py-3">
                                                <h6 className="mb-0 d-flex align-items-center" style={{ fontWeight: '600' }}>
                                                    <i className="fas fa-address-book me-2 text-muted"></i>
                                                    Contact Information
                                                </h6>
                                            </div>
                                            <div className="card-body p-4">
                                                <div className="row g-3">
                                                    <div className="col-12">
                                                        <div className="d-flex align-items-center">
                                                            <i className="fas fa-user me-3 text-muted"></i>
                                                            <div>
                                                                <small className="text-muted d-block">Contact Person</small>
                                                                <span style={{ fontWeight: '500' }}>{groupData.ContactName || 'Not specified'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-12">
                                                        <div className="d-flex align-items-center">
                                                            <i className="fas fa-phone me-3 text-muted"></i>
                                                            <div>
                                                                <small className="text-muted d-block">Phone</small>
                                                                <span style={{ fontWeight: '500' }}>{groupData.ContactNumber || 'Not specified'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-12">
                                                        <div className="d-flex align-items-center">
                                                            <i className="fas fa-envelope me-3 text-muted"></i>
                                                            <div>
                                                                <small className="text-muted d-block">Email</small>
                                                                <span style={{ fontWeight: '500' }}>{groupData.Email || 'Not specified'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-12">
                                                        <div className="d-flex align-items-center">
                                                            <i className="fas fa-briefcase me-3 text-muted"></i>
                                                            <div>
                                                                <small className="text-muted d-block">Designation</small>
                                                                <span style={{ fontWeight: '500' }}>{groupData.Designation || 'Not specified'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Address Information */}
                                    <div className="col-lg-6">
                                        <div className="card border h-100" style={{ borderRadius: '8px' }}>
                                            <div className="card-header bg-white border-bottom px-4 py-3">
                                                <h6 className="mb-0 d-flex align-items-center" style={{ fontWeight: '600' }}>
                                                    <i className="fas fa-map-marker-alt me-2 text-muted"></i>
                                                    Address Details
                                                </h6>
                                            </div>
                                            <div className="card-body p-4">
                                                <div className="mb-3 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                                                    <small className="text-muted d-block">Full Address</small>
                                                    <span style={{ fontWeight: '500' }}>{groupData.GroupAddress}</span>
                                                </div>
                                                <div className="row g-3">
                                                    <div className="col-6">
                                                        <small className="text-muted d-block">Area</small>
                                                        <span style={{ fontWeight: '500' }}>{groupData.Area || 'Not specified'}</span>
                                                    </div>
                                                    <div className="col-6">
                                                        <small className="text-muted d-block">Colony</small>
                                                        <span style={{ fontWeight: '500' }}>{groupData.Colony || 'Not specified'}</span>
                                                    </div>
                                                    <div className="col-6">
                                                        <small className="text-muted d-block">Pincode</small>
                                                        <span style={{ fontWeight: '500' }}>{groupData.Pincode || 'Not specified'}</span>
                                                    </div>
                                                    <div className="col-6">
                                                        <small className="text-muted d-block">Product ID</small>
                                                        <span style={{ fontWeight: '500' }}>{groupData.ProductId || 'Not assigned'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* QR Code Section */}
                                {groupData.QrCode && (
                                    <div className="mt-4">
                                        <div className="card border" style={{ borderRadius: '8px' }}>
                                            <div className="card-header bg-white border-bottom px-4 py-3">
                                                <h6 className="mb-0 d-flex align-items-center" style={{ fontWeight: '600' }}>
                                                    <i className="fas fa-qrcode me-2 text-muted"></i>
                                                    QR Code
                                                </h6>
                                            </div>
                                            <div className="card-body text-center p-4">
                                                <img
                                                    src={`data:image/png;base64,${groupData.QrCode}`}
                                                    alt="QR Code"
                                                    style={{
                                                        imageRendering: 'pixelated',
                                                        height: '200px',
                                                        width: '200px',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Products Section */}
                    <div className="col-12">
                        <div className="card shadow-sm border-0" style={{ borderRadius: '12px' }}>
                            <div className="card-header bg-white border-bottom px-4 py-3" style={{ borderRadius: '12px 12px 0 0' }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0 d-flex align-items-center" style={{ fontWeight: '600' }}>
                                        <i className="fas fa-box me-2"></i>
                                        Group Products ({groupProducts.length})
                                    </h5>
                                </div>
                            </div>

                            <div className="card-body p-4">
                                {/* Add Products Section */}
                                {showAddProducts && (
                                    <div ref={sectionRef} className="mb-5 p-4 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                                        <h6 className="mb-3" style={{ fontWeight: '600' }}>
                                            Add New Products to {groupData.GroupName}
                                        </h6>

                                        {/* Filter Options */}
                                        <div className="mb-4">
                                            <div className="d-flex gap-2 flex-wrap">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="productFilter"
                                                        id="allProducts"
                                                        value="all"
                                                        checked={productFilter === 'all'}
                                                        onChange={(e) => setProductFilter(e.target.value)}
                                                    />
                                                    <label className="form-check-label" htmlFor="allProducts">
                                                        All Products
                                                    </label>
                                                </div>
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="productFilter"
                                                        id="comboProducts"
                                                        value="combo"
                                                        checked={productFilter === 'combo'}
                                                        onChange={(e) => setProductFilter(e.target.value)}
                                                    />
                                                    <label className="form-check-label" htmlFor="comboProducts">
                                                        Combo Products
                                                    </label>
                                                </div>
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="productFilter"
                                                        id="individualProducts"
                                                        value="individual"
                                                        checked={productFilter === 'individual'}
                                                        onChange={(e) => setProductFilter(e.target.value)}
                                                    />
                                                    <label className="form-check-label" htmlFor="individualProducts">
                                                        Individual Products
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Available Products */}
                                        <div className="mb-4" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                            {productLoading ? (
                                                <div className="text-center p-4">
                                                    <div className="spinner-border spinner-border-sm me-2"></div>
                                                    Loading products...
                                                </div>
                                            ) : filteredProducts.length > 0 ? (
                                                <div className="row g-3">
                                                    {filteredProducts.map(product => (
                                                        <div key={product.ProductsId} className="col-md-6">
                                                            <div
                                                                className={`card border ${selectedProducts.includes(product.ProductsId) ? 'border-primary' : 'border-light'}`}
                                                                style={{ cursor: 'pointer', borderRadius: '8px' }}
                                                                onClick={() => handleProductSelect(product.ProductsId)} // Card click
                                                            >
                                                                <div className="card-body p-3">
                                                                    <div className="form-check">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            checked={selectedProducts.includes(product.ProductsId)}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation(); // 👈 prevent card click firing again
                                                                                handleProductSelect(product.ProductsId); // 👈 toggle via checkbox
                                                                            }}
                                                                            readOnly // 👈 ensures state is controlled
                                                                        />
                                                                        <label className="form-check-label">
                                                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                                                <strong>{product.ProductName}</strong>
                                                                                {product.IsCombo && <span className="badge bg-info ms-2">Combo</span>}
                                                                            </div>
                                                                            <div className="text-muted small mb-2">{product.ShortDescription}</div>
                                                                            <div className="d-flex justify-content-between align-items-center">
                                                                                {product.IsFree ? (
                                                                                    <div>
                                                                                        <span className="text-decoration-line-through text-muted me-2 small">
                                                                                            {formatCurrency(product.InsurancePremiums[0].TotalAmount)}
                                                                                        </span>
                                                                                        <span className="badge bg-success">Free</span>
                                                                                    </div>
                                                                                ) : (
                                                                                    <span className="fw-semibold text-success">
                                                                                        {formatCurrency(product.InsurancePremiums[0].TotalAmount)}
                                                                                    </span>
                                                                                )}
                                                                                <small className="text-muted">
                                                                                    {formatCurrency(product.SumAssured)} coverage
                                                                                </small>
                                                                            </div>
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            </div>



                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center text-muted p-4">
                                                    <i className="fas fa-info-circle me-2"></i>
                                                    No products available to add
                                                </div>
                                            )}
                                        </div>

                                        {/* Add Button */}
                                        <div className="d-flex justify-content-between">
                                            <button
                                                className="btn btn-outline-secondary"
                                                onClick={() => {
                                                    setShowAddProducts(false);
                                                    setSelectedProducts([]);
                                                }}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className="btn btn-primary"
                                                onClick={handleAddProducts}
                                                disabled={selectedProducts.length === 0 || productLoading}
                                            >
                                                {productLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                        Adding...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-plus me-2"></i>
                                                        Add Selected Products ({selectedProducts.length})
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                        {productError && (
                                            <div className="alert alert-danger mt-4">
                                                <i className="fas fa-exclamation-triangle me-2"></i>
                                                {productError}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Product Filters - Active/Inactive Tabs */}
                                <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex gap-2">
                                            {/* Active/Inactive Tabs */}
                                            <button
                                                className={`btn ${activeTab === 'active' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setActiveTab('active')}
                                                style={{ borderRadius: '8px' }}
                                            >
                                                <i className="fas fa-check-circle me-2"></i>
                                                Active Products
                                            </button>
                                            <button
                                                className={`btn ${activeTab === 'inactive' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                                onClick={() => setActiveTab('inactive')}
                                                style={{ borderRadius: '8px' }}
                                            >
                                                <i className="fas fa-times-circle me-2"></i>
                                                Inactive Products
                                            </button>
                                        </div>

                                        {/* Combo Filter */}
                                        <div className="d-flex gap-2">
                                            <button
                                                className={`btn btn-sm ${comboFilter === 'all' ? 'btn-info' : 'btn-outline-info'}`}
                                                onClick={() => setComboFilter('all')}
                                                style={{ borderRadius: '6px' }}
                                            >
                                                All
                                            </button>
                                            <button
                                                className={`btn btn-sm ${comboFilter === 'individual' ? 'btn-warning' : 'btn-outline-warning'}`}
                                                onClick={() => setComboFilter('individual')}
                                                style={{ borderRadius: '6px' }}
                                            >
                                                Individual
                                            </button>
                                            <button
                                                className={`btn btn-sm ${comboFilter === 'combo' ? 'btn-success' : 'btn-outline-success'}`}
                                                onClick={() => setComboFilter('combo')}
                                                style={{ borderRadius: '6px' }}
                                            >
                                                Combo
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Modern Products Table */}
                                {productLoading ? (
                                    <div className="d-flex flex-column align-items-center justify-content-center py-5" style={{ minHeight: '300px' }}>
                                        <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }}></div>
                                        <h6 className="text-muted fw-medium">Loading products...</h6>
                                    </div>
                                ) : groupProducts.length > 0 ? (
                                    <div className="responsive-table-container" style={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
                                        <div className="table-responsive" style={{ overflowX: 'auto' }}>
                                            <table className="table table-hover align-middle mb-0" style={{ backgroundColor: 'white' }}>
                                                <thead style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                                    <tr>
                                                        <th scope="col" className="px-4 py-3 fw-bold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
                                                            Product
                                                        </th>
                                                        <th scope="col" className="px-3 py-3 fw-bold text-uppercase text-center" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
                                                            Type
                                                        </th>
                                                        <th scope="col" className="px-3 py-3 fw-bold text-uppercase text-center" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
                                                            Premium
                                                        </th>
                                                        <th scope="col" className="px-3 py-3 fw-bold text-uppercase text-center" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
                                                            Coverage
                                                        </th>
                                                        <th scope="col" className="px-3 py-3 fw-bold text-uppercase text-center" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
                                                            Members
                                                        </th>
                                                        <th scope="col" className="px-3 py-3 fw-bold text-uppercase text-center" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
                                                            Age Range
                                                        </th>
                                                        <th scope="col" className="px-3 py-3 fw-bold text-uppercase text-center" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
                                                            Status
                                                        </th>
                                                        <th scope="col" className="px-3 py-3 fw-bold text-uppercase text-center" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
                                                            Actions
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {groupProducts.map((product, index) => (
                                                        <tr
                                                            key={product.ProductsId}
                                                            className="border-0"
                                                            style={{
                                                                backgroundColor: 'white',
                                                                borderBottom: index !== groupProducts.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                                transition: 'all 0.2s ease',
                                                            }}
                                                        >
                                                            <td className="px-4 py-3">
                                                                <div className="d-flex align-items-center">
                                                                    <div
                                                                        className="d-flex align-items-center justify-content-center rounded-3 me-3"
                                                                        style={{
                                                                            width: '40px',
                                                                            height: '40px',
                                                                            background: product.IsCombo
                                                                                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                                                                : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                                                            boxShadow: product.IsCombo
                                                                                ? '0 4px 14px rgba(16, 185, 129, 0.3)'
                                                                                : '0 4px 14px rgba(59, 130, 246, 0.3)',
                                                                        }}
                                                                    >
                                                                        <i
                                                                            className={`fas ${product.IsCombo ? 'fa-layer-group' : 'fa-shield-alt'}`}
                                                                            style={{
                                                                                color: 'white',
                                                                                fontSize: '16px',
                                                                            }}
                                                                        ></i>
                                                                    </div>
                                                                    <div>
                                                                        <h6 className="mb-0 fw-bold" style={{ color: '#111827', fontSize: '14px' }}>
                                                                            {product.ProductName}
                                                                        </h6>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            <td className="px-3 py-3 text-center">
                                                                <span
                                                                    className="badge rounded-pill"
                                                                    style={{
                                                                        fontSize: '10px',
                                                                        padding: '6px 12px',
                                                                        fontWeight: '600',
                                                                        letterSpacing: '0.025em',
                                                                        background: product.IsCombo
                                                                            ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
                                                                            : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                                                                        color: 'white',
                                                                        boxShadow: product.IsCombo
                                                                            ? '0 2px 8px rgba(251, 191, 36, 0.3)'
                                                                            : '0 2px 8px rgba(139, 92, 246, 0.3)',
                                                                    }}
                                                                >
                                                                    {product.IsCombo ? 'COMBO' : 'INDIVIDUAL'}
                                                                </span>
                                                            </td>

                                                            <td className="px-3 py-3 text-center">
                                                                {product.IsFree ? (
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        <span className="text-decoration-line-through text-muted" style={{ fontSize: '10px' }}>
                                                                            {formatCurrency(product.InsurancePremiums[0]?.TotalAmount || 0)}
                                                                        </span>
                                                                        <span
                                                                            className="badge rounded-pill mt-1"
                                                                            style={{
                                                                                fontSize: '9px',
                                                                                fontWeight: '700',
                                                                                padding: '5px 10px',
                                                                                background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
                                                                                color: 'white',
                                                                                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                                                                            }}
                                                                        >
                                                                            FREE
                                                                        </span>
                                                                    </div>
                                                                ) : (
                                                                    <span className="fw-bold" style={{ color: '#059669', fontSize: '13px' }}>
                                                                        {formatCurrency(product.InsurancePremiums[0]?.TotalAmount || 0)}
                                                                    </span>
                                                                )}
                                                            </td>

                                                            <td className="px-3 py-3 text-center">
                                                                <span className="fw-bold" style={{ color: '#0891b2', fontSize: '13px' }}>
                                                                    {formatCurrency(product.SumAssured)}
                                                                </span>
                                                            </td>

                                                            <td className="px-3 py-3 text-center">
                                                                <div
                                                                    className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                                                    style={{
                                                                        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                                                                        border: '2px solid #cbd5e1',
                                                                        width: '36px',
                                                                        height: '36px',
                                                                        fontSize: '12px',
                                                                        fontWeight: '700',
                                                                        color: '#374151',
                                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                                    }}
                                                                >
                                                                    {product.MaximumMembers}
                                                                </div>
                                                            </td>

                                                            <td className="px-3 py-3 text-center">
                                                                <div className="d-flex flex-column align-items-center">
                                                                    <span className="fw-semibold" style={{ color: '#6b7280', fontSize: '12px' }}>
                                                                        {product.MinimumAge} - {product.MaximumAge}
                                                                    </span>
                                                                    <span className="text-muted" style={{ fontSize: '10px' }}>years</span>
                                                                </div>
                                                            </td>

                                                            <td className="px-3 py-3 text-center">
                                                                <span
                                                                    className="badge rounded-pill"
                                                                    style={{
                                                                        fontSize: '10px',
                                                                        padding: '6px 12px',
                                                                        fontWeight: '600',
                                                                        letterSpacing: '0.025em',
                                                                        background: product.IsActive
                                                                            ? 'linear-gradient(135deg, #10b981 0%, #047857 100%)'
                                                                            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                                                        color: 'white',
                                                                        boxShadow: product.IsActive
                                                                            ? '0 2px 8px rgba(16, 185, 129, 0.3)'
                                                                            : '0 2px 8px rgba(239, 68, 68, 0.3)',
                                                                    }}
                                                                >
                                                                    {product.IsActive ? 'ACTIVE' : 'INACTIVE'}
                                                                </span>
                                                            </td>

                                                            <td className="px-3 py-3">
                                                                <div className="d-flex justify-content-center gap-2">
                                                                    <button
                                                                        className="btn btn-outline-primary btn-sm d-flex align-items-center"
                                                                        onClick={() => handleProductDetailsNavigation(product.ProductsId)}
                                                                        style={{
                                                                            borderRadius: '6px',
                                                                            fontSize: '10px',
                                                                            fontWeight: '600',
                                                                            padding: '6px 12px',
                                                                            border: '2px solid #3b82f6',
                                                                            color: '#3b82f6',
                                                                            transition: 'all 0.2s ease',
                                                                        }}
                                                                        title="View Details"
                                                                    >
                                                                        <i className="fas fa-eye me-1"></i>
                                                                        View
                                                                    </button>
                                                                    {activeTab === 'active' && (
                                                                        <button
                                                                            className="btn btn-outline-danger btn-sm d-flex align-items-center"
                                                                            onClick={() => handleRemoveProduct(product.GroupProductsId)}
                                                                            style={{
                                                                                borderRadius: '6px',
                                                                                fontSize: '10px',
                                                                                fontWeight: '600',
                                                                                padding: '6px 12px',
                                                                                border: '2px solid #ef4444',
                                                                                color: '#ef4444',
                                                                                transition: 'all 0.2s ease',
                                                                            }}
                                                                            title="Remove Product"
                                                                        >
                                                                            <i className="fas fa-trash me-1"></i>
                                                                            Remove
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column align-items-center justify-content-center py-5" style={{ minHeight: '400px' }}>
                                        <div
                                            className="mb-4 d-flex align-items-center justify-content-center rounded-circle"
                                            style={{
                                                width: '100px',
                                                height: '100px',
                                                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                                                border: '3px solid #cbd5e1',
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                            }}
                                        >
                                            <i className="fas fa-box-open fa-2x" style={{ color: '#9ca3af' }}></i>
                                        </div>
                                        <h5 className="text-dark mb-2 fw-bold">No {activeTab} Products Found</h5>
                                        <p className="text-muted mb-4 text-center" style={{ maxWidth: '350px', fontSize: '13px', lineHeight: '1.5' }}>
                                            This group doesn't have any {activeTab} {comboFilter !== 'all' ? comboFilter : ''} products assigned yet.
                                        </p>
                                        {activeTab === 'active' && (
                                            <button
                                                className="btn btn-primary d-flex align-items-center"
                                                onClick={() => setShowAddProducts(true)}
                                                style={{
                                                    borderRadius: '10px',
                                                    padding: '10px 20px',
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                                    border: 'none',
                                                    boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                                                    transition: 'all 0.2s ease',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.3)';
                                                }}
                                            >
                                                <i className="fas fa-plus me-2"></i>
                                                Add Products
                                            </button>
                                        )}
                                    </div>
                                )}

                                {productError && (
                                    <div className="alert alert-danger mt-4">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        {productError}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>


                    <div className="col-12">
                        <div className="card shadow-sm border-0" style={{ borderRadius: '12px' }}>
                            <div className="card-header bg-white border-bottom px-4 py-3" style={{ borderRadius: '12px 12px 0 0' }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0 d-flex align-items-center" style={{ fontWeight: '600' }}>
                                        <i className="fas fa-users me-2"></i>
                                        Group Advisors ({groupAdvisors.length})
                                    </h5>
                                    {!showAddAdvisors && (
                                        <button
                                            className="btn btn-primary d-flex align-items-center"
                                            onClick={() => setShowAddAdvisors(true)}
                                            style={{ borderRadius: '8px', fontSize: '13px' }}
                                        >
                                            <i className="fas fa-user-plus me-2"></i>
                                            Add Advisors
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="card-body p-4">
                                {/* Add Advisors Section */}
                                {showAddAdvisors && (
                                    <div ref={sectionRef} className="mb-5 p-4 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                                        <h6 className="mb-3" style={{ fontWeight: '600' }}>
                                            Add New Advisors to {groupData.GroupName}
                                        </h6>

                                        {/* Available Advisors */}
                                        <div className="mb-4" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                            {loading ? (
                                                <div className="text-center p-4">
                                                    <div className="spinner-border spinner-border-sm me-2"></div>
                                                    Loading advisors...
                                                </div>
                                            ) : allAdvisors.length > 0 ? (
                                                <div className="row g-3">
                                                    {allAdvisors.map(advisor => (
                                                        <div key={advisor.EmployeeId} className="col-md-6">
                                                            <div
                                                                className={`card border ${selectedAdvisors.includes(advisor.EmployeeId) ? 'border-primary' : 'border-light'}`}
                                                                style={{ cursor: 'pointer', borderRadius: '8px' }}
                                                                onClick={() => handleAdvisorSelect(advisor.EmployeeId)}
                                                            >
                                                                <div className="card-body p-3">
                                                                    <div className="form-check">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            checked={selectedAdvisors.includes(advisor.EmployeeId)}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleAdvisorSelect(advisor.EmployeeId);
                                                                            }}
                                                                            readOnly
                                                                        />
                                                                        <label className="form-check-label">
                                                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                                                <strong>{advisor.FullName}</strong>
                                                                                <span className="badge bg-info ms-2">{advisor.EmployeeCode}</span>
                                                                            </div>
                                                                            <div className="text-muted small mb-2">{advisor.MobileNumber}</div>
                                                                            <div className="d-flex justify-content-between align-items-center">
                                                                                <small className="text-muted">
                                                                                    {advisor.DistrictName}, {advisor.StateName}
                                                                                </small>
                                                                                <small className="text-muted">
                                                                                    Age: {advisor.Age}
                                                                                </small>
                                                                            </div>
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center text-muted p-4">
                                                    <i className="fas fa-info-circle me-2"></i>
                                                    No advisors available to add
                                                </div>
                                            )}
                                        </div>

                                        {/* Add Button */}
                                        <div className="d-flex justify-content-between">
                                            <button
                                                className="btn btn-outline-secondary"
                                                onClick={() => {
                                                    setShowAddAdvisors(false);
                                                    setSelectedAdvisors([]);
                                                }}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className="btn btn-primary"
                                                onClick={handleAddAdvisors}
                                                disabled={selectedAdvisors.length === 0 || advisorLoading}
                                            >
                                                {advisorLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                        Adding...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-plus me-2"></i>
                                                        Add Selected Advisors ({selectedAdvisors.length})
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                        {error && (
                                            <div className="alert alert-danger mt-4">
                                                <i className="fas fa-exclamation-triangle me-2"></i>
                                                {error}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Advisor Filters - Active/Inactive Tabs */}
                                {/* <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex gap-2">
                                            
                                            <button
                                                className={`btn ${activeAdviosrTab === 'active' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setActiveAdviosrTab('active')}
                                                style={{ borderRadius: '8px' }}
                                            >
                                                <i className="fas fa-check-circle me-2"></i>
                                                Active Advisors
                                            </button>
                                            <button
                                                className={`btn ${activeAdviosrTab === 'inactive' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                                onClick={() => setActiveAdviosrTab('inactive')}
                                                style={{ borderRadius: '8px' }}
                                            >
                                                <i className="fas fa-times-circle me-2"></i>
                                                Inactive Advisors
                                            </button>
                                        </div>
                                    </div>
                                </div> */}


                                {loading ? (
                                    <div className="d-flex flex-column align-items-center justify-content-center py-5" style={{ minHeight: '300px' }}>
                                        <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }}></div>
                                        <h6 className="text-muted fw-medium">Loading advisors...</h6>
                                    </div>
                                ) : groupAdvisors.length > 0 ? (
                                    <div className="responsive-table-container" style={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
                                        <div className="table-responsive" style={{ overflowX: 'auto' }}>
                                            <table className="table table-hover align-middle mb-0" style={{ backgroundColor: 'white' }}>
                                                <thead style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                                    <tr>
                                                        <th scope="col" className="px-4 py-3 fw-bold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
                                                            Advisor
                                                        </th>
                                                        <th scope="col" className="px-3 py-3 fw-bold text-uppercase text-center" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
                                                            Code
                                                        </th>
                                                        <th scope="col" className="px-3 py-3 fw-bold text-uppercase text-center" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
                                                            Mobile
                                                        </th>
                                                        <th scope="col" className="px-3 py-3 fw-bold text-uppercase text-center" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
                                                            Location
                                                        </th>
                                                        <th scope="col" className="px-3 py-3 fw-bold text-uppercase text-center" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
                                                            Age
                                                        </th>
                                                        <th scope="col" className="px-3 py-3 fw-bold text-uppercase text-center" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
                                                            Reporting To
                                                        </th>
                                                        <th scope="col" className="px-3 py-3 fw-bold text-uppercase text-center" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
                                                            Status
                                                        </th>
                                                        <th scope="col" className="px-3 py-3 fw-bold text-uppercase text-center" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#64748b' }}>
                                                            Actions
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {groupAdvisors.map((advisor, index) => (
                                                        <tr
                                                            key={advisor.GroupAdvisorsId}
                                                            className="border-0"
                                                            style={{
                                                                backgroundColor: 'white',
                                                                borderBottom: index !== groupAdvisors.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                                transition: 'all 0.2s ease',
                                                            }}
                                                        >
                                                            <td className="px-4 py-3">
                                                                <div className="d-flex align-items-center">
                                                                    <div
                                                                        className="d-flex align-items-center justify-content-center rounded-3 me-3"
                                                                        style={{
                                                                            width: '40px',
                                                                            height: '40px',
                                                                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                                                            boxShadow: '0 4px 14px rgba(59, 130, 246, 0.3)',
                                                                        }}
                                                                    >
                                                                        <i
                                                                            className="fas fa-user"
                                                                            style={{
                                                                                color: 'white',
                                                                                fontSize: '16px',
                                                                            }}
                                                                        ></i>
                                                                    </div>
                                                                    <div>
                                                                        <h6 className="mb-0 fw-bold" style={{ color: '#111827', fontSize: '14px' }}>
                                                                            {advisor.FullName}
                                                                        </h6>
                                                                        <small className="text-muted">{advisor.Gender}</small>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            <td className="px-3 py-3 text-center">
                                                                <span className="badge bg-info" style={{ fontSize: '10px', padding: '4px 8px' }}>
                                                                    {advisor.EmployeeCode}
                                                                </span>
                                                            </td>

                                                            <td className="px-3 py-3 text-center">
                                                                <span className="fw-semibold" style={{ color: '#0891b2', fontSize: '12px' }}>
                                                                    {advisor.MobileNumber}
                                                                </span>
                                                            </td>

                                                            <td className="px-3 py-3 text-center">
                                                                <div className="d-flex flex-column align-items-center">
                                                                    <span className="fw-semibold" style={{ color: '#6b7280', fontSize: '11px' }}>
                                                                        {advisor.DistrictName}
                                                                    </span>
                                                                    <span className="text-muted" style={{ fontSize: '10px' }}>{advisor.StateName}</span>
                                                                </div>
                                                            </td>

                                                            <td className="px-3 py-3 text-center">
                                                                <div
                                                                    className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                                                    style={{
                                                                        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                                                                        border: '2px solid #cbd5e1',
                                                                        width: '36px',
                                                                        height: '36px',
                                                                        fontSize: '12px',
                                                                        fontWeight: '700',
                                                                        color: '#374151',
                                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                                    }}
                                                                >
                                                                    {advisor.Age}
                                                                </div>
                                                            </td>

                                                            <td className="px-3 py-3 text-center">
                                                                <span className="text-muted" style={{ fontSize: '11px' }}>
                                                                    {advisor.ReportingName}
                                                                </span>
                                                            </td>

                                                            <td className="px-3 py-3 text-center">
                                                                <span
                                                                    className="badge rounded-pill"
                                                                    style={{
                                                                        fontSize: '10px',
                                                                        padding: '6px 12px',
                                                                        fontWeight: '600',
                                                                        letterSpacing: '0.025em',
                                                                        background: advisor.IsGroupAdvisorActive
                                                                            ? 'linear-gradient(135deg, #10b981 0%, #047857 100%)'
                                                                            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                                                        color: 'white',
                                                                        boxShadow: advisor.IsGroupAdvisorActive
                                                                            ? '0 2px 8px rgba(16, 185, 129, 0.3)'
                                                                            : '0 2px 8px rgba(239, 68, 68, 0.3)',
                                                                    }}
                                                                >
                                                                    {advisor.IsGroupAdvisorActive ? 'ACTIVE' : 'INACTIVE'}
                                                                </span>
                                                            </td>

                                                            <td className="px-3 py-3">
                                                                <div className="d-flex justify-content-center gap-2">
                                                                    {activeAdviosrTab === 'active' ? (
                                                                        <button
                                                                            className="btn btn-outline-danger btn-sm d-flex align-items-center"
                                                                            onClick={() => handleRemoveAdvisor(advisor.GroupAdvisorsId)}
                                                                            style={{
                                                                                borderRadius: '6px',
                                                                                fontSize: '10px',
                                                                                fontWeight: '600',
                                                                                padding: '6px 12px',
                                                                                border: '2px solid #ef4444',
                                                                                color: '#ef4444',
                                                                                transition: 'all 0.2s ease',
                                                                            }}
                                                                            title="Delete Advisor"
                                                                        >
                                                                            <i className="fas fa-trash-alt me-1"></i>
                                                                            Delete
                                                                        </button>
                                                                    ) : (
                                                                        <button
                                                                            className="btn btn-outline-success btn-sm d-flex align-items-center"
                                                                            onClick={() => handleActivateAdvisor(advisor.GroupAdvisorsId)}
                                                                            style={{
                                                                                borderRadius: '6px',
                                                                                fontSize: '10px',
                                                                                fontWeight: '600',
                                                                                padding: '6px 12px',
                                                                                border: '2px solid #10b981',
                                                                                color: '#10b981',
                                                                                transition: 'all 0.2s ease',
                                                                            }}
                                                                            title="Activate Advisor"
                                                                        >
                                                                            <i className="fas fa-user-check me-1"></i>
                                                                            Activate
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column align-items-center justify-content-center py-5" style={{ minHeight: '400px' }}>
                                        <div
                                            className="mb-4 d-flex align-items-center justify-content-center rounded-circle"
                                            style={{
                                                width: '100px',
                                                height: '100px',
                                                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                                                border: '3px solid #cbd5e1',
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                            }}
                                        >
                                            <i className="fas fa-users fa-2x" style={{ color: '#9ca3af' }}></i>
                                        </div>
                                        <h5 className="text-dark mb-2 fw-bold">No {activeAdviosrTab} Advisors Found</h5>
                                        <p className="text-muted mb-4 text-center" style={{ maxWidth: '350px', fontSize: '13px', lineHeight: '1.5' }}>
                                            This group doesn't have any {activeAdviosrTab} advisors assigned yet.
                                        </p>
                                        {activeAdviosrTab === 'active' && !showAddAdvisors && (
                                            <button
                                                className="btn btn-primary d-flex align-items-center"
                                                onClick={() => setShowAddAdvisors(true)}
                                                style={{
                                                    borderRadius: '10px',
                                                    padding: '10px 20px',
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                                    border: 'none',
                                                    boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                                                    transition: 'all 0.2s ease',
                                                }}
                                            >
                                                <i className="fas fa-user-plus me-2"></i>
                                                Add Advisors
                                            </button>
                                        )}
                                    </div>
                                )}

                                {error && (
                                    <div className="alert alert-danger mt-4">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        {error}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modern Professional Styles */}
            <style jsx>{`
                .table {
                    border-collapse: separate;
                    border-spacing: 0;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }
                
                .table thead th {
                    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                    border: none;
                    font-weight: 600;
                    color: #495057;
                    position: sticky;
                    top: 0;
                    z-index: 10;
                }
                
                .table tbody tr {
                    border-bottom: 1px solid #f1f3f4;
                    transition: all 0.2s ease;
                }
                
                .table tbody tr:hover {
                    background-color: #f8f9ff !important;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                
                .table tbody tr:last-child {
                    border-bottom: none;
                }
                
                .card {
                    transition: all 0.3s ease;
                    border: 1px solid #e9ecef;
                }
                
                .card:hover {
                    box-shadow: 0 8px 25px rgba(0,0,0,0.1);
                    transform: translateY(-2px);
                }
                
                .btn {
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                
                .btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }
                
                .badge {
                    font-weight: 500;
                    letter-spacing: 0.025em;
                }
                
                .form-check-input:checked {
                    background-color: #0d6efd;
                    border-color: #0d6efd;
                }
                
                .icon-circle {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.2rem;
                }
                
                .table-responsive {
                    border-radius: 12px;
                    overflow: hidden;
                }
                
                .spinner-border {
                    border-width: 0.15em;
                }
                
                @media (max-width: 768px) {
                    .container-fluid {
                        padding: 1rem;
                    }
                    
                    h1 {
                        font-size: 1.5rem !important;
                    }
                    
                    .card-body {
                        padding: 1.5rem !important;
                    }
                    
                    .table-responsive {
                        font-size: 0.9rem;
                    }
                    
                    .btn-sm {
                        padding: 0.25rem 0.5rem;
                        font-size: 0.75rem;
                    }
                    
                    .d-flex.gap-2 {
                        flex-wrap: wrap;
                    }
                    
                    .badge {
                        font-size: 0.7rem !important;
                    }
                }
                
                @media (max-width: 576px) {
                    .table thead {
                        display: none;
                    }
                    
                    .table tbody tr {
                        display: block;
                        border: 1px solid #dee2e6;
                        border-radius: 8px;
                        margin-bottom: 1rem;
                        padding: 1rem;
                        background: white;
                    }
                    
                    .table tbody td {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        padding: 0.5rem 0 !important;
                        border: none;
                    }
                    
                    .table tbody td::before {
                        content: attr(data-label);
                        font-weight: 600;
                        color: #6c757d;
                        text-transform: uppercase;
                        font-size: 0.75rem;
                        letter-spacing: 0.05em;
                    }
                }
            `}</style>
        </div>
    );
};

export default GroupPersonalDetails;